import i18n from '@/i18n';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { components } from '@/api/schema';
import { apiClient } from '@/lib/api-client';
import { apiErrorMessage } from '@/lib/api-error';

export type GlobalProduct = components['schemas']['GlobalProductResource'];

export interface GlobalProductFilters {
  search?: string;
  category?: string;
  guruh_nomi?: string;
  page?: number;
  per_page?: number;
}

interface GlobalProductPage {
  data?: GlobalProduct[];
  meta?: { current_page: number; last_page: number; per_page: number; total: number };
  groups?: string[];
}

/** FR-98: the platform's global catalog, page by page (server-side search and filters, as Lovable). */
export function useGlobalProducts(filters: GlobalProductFilters) {
  return useQuery({
    queryKey: ['/global-products', {
      params: {
        query: {
          search: filters.search?.trim() || undefined,
          category: filters.category && filters.category !== 'all' ? filters.category : undefined,
          guruh_nomi: filters.guruh_nomi && filters.guruh_nomi !== 'all' ? filters.guruh_nomi : undefined,
          page: filters.page,
          per_page: filters.per_page,
        } as never,
      },
    }] as const,
    placeholderData: (prev) => prev,
    select: (res: GlobalProductPage) => ({
      items: res?.data ?? [],
      total: res?.meta?.total ?? res?.data?.length ?? 0,
      lastPage: res?.meta?.last_page ?? 1,
      groups: res?.groups ?? [],
    }),
  });
}

/**
 * Lovable's manage-products `bulk_assign`: copies global products into a merchant
 * catalog (a merchant always into its own; a platform admin passes merchantId).
 */
export function useAssignGlobalProducts() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ productIds, merchantId }: { productIds: number[]; merchantId?: number }) => {
      const { data, error } = await apiClient.POST('/global-products/assign', {
        body: { product_ids: productIds, merchant_id: merchantId } as never,
      });
      if (error) throw new Error(apiErrorMessage(error, i18n.t('misc_refund.error')));
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['/products'] }),
  });
}
